/*
 * CarouselComponent
  * This component implements a responsive carousel that allows users to navigate through a list of items.
  * It supports touch and mouse interactions, auto-sliding, and custom animations.
  * The carousel can be used to display images, cards, or any other content in a sliding format.
  * It emits events when the slide changes or is clicked, allowing for further interaction.
    
  *Features:
  * - Touch and mouse swipe support for navigation
  * - Auto-slide functionality with customizable intervals
  * - Customizable animation duration
  * - Click events on slides
  * - Responsive design with indicators for current slide
  * - Supports both horizontal and vertical swipes
  * - TrackBy function for performance optimization
  * - Reset auto-slide on user interaction
  * - Jump to specific slide functionality
  * - Change detection strategy for performance optimization
  * - Input properties for customization

  * //=====PLEASE DON'T MAKE CHANGES TO THIS FILE UNLESS YOU ARE SURE ABOUT THE FUNCTIONALITY AND ITS IMPACT ON THE APPLICATION.=========
 */


import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ContentChild, TemplateRef } from '@angular/core';

import { IonicModule } from "@ionic/angular";
@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarouselComponent implements OnInit, OnDestroy {

  //==================Callbacks========================
  @Output() slideChanged = new EventEmitter<number>();
  @Output() slideClicked = new EventEmitter<any>();

  //==================Inputs========================
  @Input() items: any[] = [];
  @Input() animationDuration = 500;
  @Input() showIndicators = true;
  @Input() autoSlide = false;
  @Input() slideInterval = 3000;
  @ContentChild('slideTemplate', { static: false }) slideTemplate?: TemplateRef<any>;

  //==================Properties======================== +21
  public currentIndex = 0;
  private isDragging = false;
  private startX = 0;
  private startY = 0;
  private deltaX = 0;
  private deltaY = 0;
  private readonly SWIPE_THRESHOLD = 50;
  private hasDragged = false;
  private autoSlideInterval: any;
  private isHorizontalSwipe: boolean | null = null;

  constructor(private cdr: ChangeDetectorRef) { }
  ngOnInit() {
    if (this.autoSlide) {
      this.startAutoSlide();
    }
  }

  ngOnDestroy() {
    this.stopAutoSlide();
  }


  startAutoSlide() {
    this.autoSlideInterval = setInterval(() => this.moveToNext(), this.slideInterval);
  }

  stopAutoSlide() {
    clearInterval(this.autoSlideInterval);
  }


  //===========  Resets the auto slide functionality by stopping and restarting it. This is useful when the user interacts with the carousel, such as clicking or swiping.=========
  resetAutoSlide() {
    if (this.autoSlide) {
      this.stopAutoSlide();
      this.startAutoSlide();
    }
  }

  //=========== Moves to the next slide and emits the current index. If there is only one item, it does nothing.=========
  moveToNext() {
    this.resetAutoSlide();
    if (this.items.length <= 1) return;
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.slideChanged.emit(this.currentIndex);
    this.cdr.markForCheck();
  }

  //=========== Moves to the previous slide and emits the current index. If there is only one item, it does nothing.=========
  moveToPrevious() {
    this.resetAutoSlide();
    if (this.items.length <= 1) return;
    this.currentIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
    this.slideChanged.emit(this.currentIndex);
    this.cdr.markForCheck();
  }

  //=========== Handles the click event on the slide. If the user has not dragged, it emits the clicked item.=========
  onSlideClick() {
    if (!this.hasDragged) {
      this.slideClicked.emit(this.items[this.currentIndex]);
    }

    this.hasDragged = false;
  }


  private setTouchStart(event: TouchEvent) {
    this.startX = event.touches[0].clientX;
    this.startY = event.touches[0].clientY;
    this.deltaX = 0;
    this.deltaY = 0;
    this.isHorizontalSwipe = null;
  }

  private updateTouchDeltas(event: TouchEvent) {
    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    this.deltaX = currentX - this.startX;
    this.deltaY = currentY - this.startY;
  }

  private detectTouchDirection(threshold = 15) {
    if (this.isHorizontalSwipe === null) {
      if (Math.abs(this.deltaX) > threshold || Math.abs(this.deltaY) > threshold) {
        this.isHorizontalSwipe = Math.abs(this.deltaX) > Math.abs(this.deltaY);
      }
    }
  }

  handleTouchStart(event: TouchEvent) {
    this.isDragging = true;
    this.hasDragged = false;
    this.setTouchStart(event);
    if (this.autoSlide) this.stopAutoSlide();
  }

  handleTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;
    this.updateTouchDeltas(event);
    this.detectTouchDirection();
    if (this.isHorizontalSwipe) {
      event.preventDefault();
    }
  }

  handleTouchEnd(event: TouchEvent) {
    if (!this.isDragging) return;
    this.hasDragged = Math.abs(this.deltaX) > this.SWIPE_THRESHOLD;
    if (this.isHorizontalSwipe) {
      if (this.deltaX > this.SWIPE_THRESHOLD) {
        this.moveToPrevious();
      } else if (this.deltaX < -this.SWIPE_THRESHOLD) {
        this.moveToNext();
      } else {
        this.slideClicked.emit(this.items[this.currentIndex]);
      }
    }
    this.isDragging = false;
    this.deltaX = 0;
    this.deltaY = 0;
    this.isHorizontalSwipe = null;
  }

  //=========== Handles the pointer down event to start dragging. It also stops the auto slide if enabled.=========
  handlePointerDown(event: TouchEvent | MouseEvent) {
    // if it’s a mouse event and NOT the primary (left) button, ignore
    if (event instanceof MouseEvent && event.button !== 0) return;

    this.isDragging = true;
    this.hasDragged = false;
    this.startX = event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
    if (this.autoSlide) this.stopAutoSlide();
    event.preventDefault();
  }

  //=========== Handles the pointer move event to update the dragging state and calculate the delta X.=========
  handlePointerMove(event: TouchEvent | MouseEvent) {
    if (!this.isDragging) return;
    const x = event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
    this.deltaX = x - this.startX;
    event.preventDefault();
  }

  //=========== Handles the pointer up event to determine if the user has dragged enough to change slides. If not, it emits the clicked item.=========
  handlePointerUp(event: PointerEvent | TouchEvent | MouseEvent) {
    // ignore right/middle taps for mouse events
    if (event instanceof MouseEvent && event.button !== 0) {
      this.isDragging = false;
      this.deltaX = 0;
      return;
    }
    if (!this.isDragging) return;
    this.hasDragged = Math.abs(this.deltaX) > this.SWIPE_THRESHOLD;
    if (this.deltaX > this.SWIPE_THRESHOLD) {
      this.moveToPrevious();
    } else if (this.deltaX < -this.SWIPE_THRESHOLD) {
      this.moveToNext();
    }
    else {
      // this.resetAutoSlide();
      this.slideClicked.emit(this.items[this.currentIndex]);
    }
    this.isDragging = false;
    this.deltaX = 0;

  }


  //===========Getter to focus on current slide and translate the wrapper to the current index.=========
  get translateXValue(): string {
    return `translateX(-${this.currentIndex * 100}%)`;
  }

  //===========Getter to apply the transition effect to the wrapper based on the animation duration.=========
  get wrapperTransition(): string {
    return `transform ${this.animationDuration}ms ease-in-out`;
  }

  //===========TrackBy function for ngFor to optimize performance by tracking items by their index.=========
  trackById(index: number): number {
    return index;
  }

  //==============Jump to a specific slide based on the index and reset the auto slide.=========
  jumpToSlide(index: number) {
    this.resetAutoSlide();
    this.currentIndex = index;
    this.slideChanged.emit(this.currentIndex);
    this.cdr.markForCheck();
  }
}